import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, cn } from '@scio3/ui';
import { useSubscriptionModal } from '@/hooks/useSubscriptionModal';
import { useAuthStore } from '@/stores/auth-store';
import { analyticsUpgradeCta } from '@/lib/analytics-service';
import { openUpgradeWizard } from '@/features/billing/stores/billing-dialog-store';
import { toast } from 'sonner';

const useNativeBilling = import.meta.env.VITE_USE_NATIVE_BILLING === 'true';

export interface PlanGateProps {
  /** Feature flag key passed to the subscription modal (e.g. 'samlSSO', 'apiKey'). */
  planFeature: string;
  /** Minimum plan required (e.g. 'proPlus', 'engage'). */
  minPlanRequired: string;
  /** Human-readable feature name shown in the message (e.g. 'SAML Single Sign On'). */
  featureLabel: string;
  /** Human-readable plan name shown in the message (e.g. 'Pro Plus Plan'). */
  planLabel: string;
  /** Optional "Learn more" link URL. Defaults to generic OptiSigns link. */
  learnMoreUrl?: string;
  /** Optional background image URL. When provided, shows a greyscale image behind the overlay. */
  backgroundImageUrl?: string;
  /**
   * Vertically center the message instead of anchoring it ~20% from the top.
   * Default false preserves the top-anchored layout tuned for the
   * background-image mode; pass true for a plain full-area overlay with no
   * image (both vertical + horizontal centering).
   */
  centered?: boolean;
}

/**
 * Reusable plan gate — shows an upgrade prompt when a feature is not available
 * on the user's current plan.
 *
 * Two modes:
 * - With `backgroundImageUrl`: full-area overlay with greyscale background image + semi-transparent
 *   black overlay + centered white text/button (matching scio1 plan gate design).
 * - Without: transparent overlay designed to sit on top of disabled page UI underneath.
 */
export function PlanGate({
  planFeature,
  minPlanRequired,
  featureLabel,
  planLabel,
  learnMoreUrl = 'https://links.optisigns.com/E2Ea',
  backgroundImageUrl,
  centered = false,
}: PlanGateProps) {
  const { t } = useTranslation();
  const { openSubscriptionPlan } = useSubscriptionModal();
  const company = useAuthStore((s) => s.company);
  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel;
  // Hide the background image if it fails to load (e.g. asset missing/404) so the
  // browser's broken-image placeholder never shows — the dark overlay stands on its own.
  const [imageFailed, setImageFailed] = useState(false);

  const handleUpgrade = useCallback(() => {
    const parentAccountId = (company as Record<string, unknown> | null)?.parentAccountId;
    if (parentAccountId) {
      toast.error(t('cannotUpgrade'));
      return;
    }
    analyticsUpgradeCta({
      ctaLocation: `plan_gate_${planFeature}`,
      currentPlan: company?.payment?.plan,
    });
    if (useNativeBilling) {
      openUpgradeWizard(minPlanRequired);
      return;
    }
    openSubscriptionPlan({
      title: t('Change Plan'),
      subtitle: t('Upgrade Required'),
      action: 'changePlanTrialToPay',
      sizeModal: 'xl',
      params: {
        planFeature,
        minPlanRequired,
        subtitle: t('Upgrade Required'),
        arrCssFile: true,
      },
    });
  }, [openSubscriptionPlan, company, t, planFeature, minPlanRequired]);

  const content = (
    <>
      <p className="text-center text-sm text-white">
        {t('{{feature}} is available for {{plan}}', {
          feature: t(featureLabel),
          plan: t(planLabel),
        })}
      </p>
      <Button
        size="lg"
        className="mt-3 px-7 text-sm"
        onClick={handleUpgrade}
        data-testid="plan-gate-upgrade-btn"
      >
        {t('Upgrade Now')}
      </Button>
      {!isWhiteLabel && (
        <p className="mt-3 text-center text-sm text-white">
          <a
            href={learnMoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white underline hover:text-white/80"
          >
            {t('Learn more')}
          </a>
          {' - '}
          {t('You can upgrade/downgrade anytime')}
        </p>
      )}
    </>
  );

  if (backgroundImageUrl) {
    return (
      <div data-testid="plan-gate" className="relative h-full w-full overflow-hidden rounded-lg">
        {!imageFailed && (
          <img
            src={backgroundImageUrl}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-top opacity-70 grayscale"
            onError={() => setImageFailed(true)}
          />
        )}
        <div className="absolute inset-0 bg-black/70" />
        <div
          className={cn(
            'absolute inset-0 flex flex-col items-center',
            centered ? 'justify-center' : 'justify-start pt-[20%]',
          )}
        >
          {content}
        </div>
      </div>
    );
  }

  // Overlay mode — sits on top of disabled page UI.
  // `rounded-xl` matches the standard card radius so the dark scrim's corners
  // align with the rounded container it's dropped into (e.g. SettingsLayout's
  // `rounded-xl` card) instead of poking square corners past the card edge.
  return (
    <div
      data-testid="plan-gate"
      className={cn(
        'absolute inset-0 z-50 flex flex-col items-center overflow-hidden rounded-xl',
        centered ? 'justify-center' : 'justify-start pt-[20%]',
      )}
    >
      <div className="absolute inset-0 rounded-xl bg-black/70" />
      <div className="relative z-10 flex flex-col items-center">{content}</div>
    </div>
  );
}
